import { CardBody } from 'react-bootstrap';
import { Button, Card, LinkButton, Tooltip } from '@pmi/dsm-react';
import { ArrowForwardIcon } from '@pmi/dsm-react/dist/components/icons/24';
import {
  AlertWarningIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@pmi/dsm-react/dist/components/icons/fa/16';
import { useEffect, useState } from 'react';
import { AgreementApiModel } from '../../models/DealModel';
import { ProductApiModel } from '../../models/ProductModel';
import styles from './agreementSelectedProducts.module.scss';
import { User } from '../../models/UserModels';
import RenderHtml from '../RenderHtml';

export interface AgreementSelectorSelectedProductsProps {
  products: ProductApiModel[];
  deal: AgreementApiModel;
  onClick: (products: ProductApiModel[]) => void;
  disabled: boolean;
  buttonText: string;
  people?: User[];
  emailCount?: number;
  excludedAssignments?: { personName: string; productName: string }[];
  isInviteStep?: boolean;
}

export default function AgreementSelectedProducts({
  products,
  deal,
  onClick,
  disabled,
  buttonText,
  people = undefined,
  emailCount = 0,
  excludedAssignments = [],
  isInviteStep = false,
}: AgreementSelectorSelectedProductsProps) {
  const [showContents, setShowContents] = useState(false);
  useEffect(() => {
    const modalBody: HTMLElement | null | undefined =
      document.body.querySelector('.invite-wizard .modal-body');
    if (modalBody) {
      modalBody?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, []);
  return (
    <div
      className={`round round-md ${styles.agreementSelectorSelectedProducts} ${
        showContents ? styles.agreementSelectorSelectedProductsActive : ''
      }`}
    >
      <Card>
        <CardBody className="p-0">
          <div
            className={`d-block d-sm-flex mb-2 ${styles.agreementSelectorSelectedProductsHeader}`}
          >
            <strong className="text-lg-start d-none d-sm-block">
              Product Assignment Summary
            </strong>
            <span className="btn-toggle">
              <span>Product Assignment Summary</span>
              <Button
                titleText=""
                icon={showContents ? ChevronDownIcon : ChevronUpIcon}
                variant="light"
                type="button"
                iconAlign="right"
                className="fw-bold"
                onClick={() => setShowContents(!showContents)}
              />
            </span>
          </div>
          {isInviteStep && showContents && <hr />}
          {!isInviteStep && <hr />}
          <div
            className={`${styles.agreementSelectorSelectedProductsContents} ${
              showContents
                ? styles.agreementSelectorSelectedProductsContentsShow
                : styles.agreementSelectorSelectedProductsContentsHide
            }`}
          >
            {people && people.length > 1 && (
              <>
                <div className="d-block py-2 small text-muted">
                  <strong>People</strong>
                </div>
                <div className="d-flex mb-3 gap-1 small flex-wrap text-muted">
                  {people.map((p) => (
                    <p className={styles.peopleChip}>
                      {p.invitationStatus.toLowerCase() !== 'pending'
                        ? p.personName
                        : p.email}
                    </p>
                  ))}
                </div>
                <hr className="mb-2" />
              </>
            )}
            <div className="d-block py-2 small text-muted">
              <strong>Agreement</strong>
            </div>
            <div className="d-flex mb-3 small justify-content-between text-muted">
              {deal.dealName.length > 0 && <div>{deal.dealName}</div>}
              <div>{deal.id}</div>
            </div>
            <hr className="mb-2" />
            <div className="d-block py-2 small text-muted">
              <strong>Products</strong>
            </div>
            <div className="d-block mb-3 w-100">
              <ul className={`list-unstyled ${styles.productsList}`}>
                {products.length > 0 &&
                  products.map((p) => {
                    const redeemedBy = excludedAssignments
                      .filter((e) => e.productName === p.name)
                      .map((e) => e.personName);
                    const isExcluded = redeemedBy.length > 0;
                    const item = (
                      <RenderHtml
                        className={`d-block w-100 small ${
                          isExcluded ? 'text-warning' : 'text-muted'
                        } ${styles.textEllipsis}`}
                        html={p?.name && p.name.length > 0 ? p.name : p?.sku}
                        tag="li"
                        key={`prodSelectedSafeHtml${p.sku}`}
                      />
                    );
                    return isExcluded ? (
                      <Tooltip
                        key={`prodTooltip${p.sku}`}
                        content={`Note: The product will not be unassigned for those who have already redeemed it. Redeemed by: ${redeemedBy.join(
                          ', ',
                        )}`}
                        type="sm"
                        placement="left"
                        className="darkTooltip"
                      >
                        <span className="d-flex align-items-center gap-1">
                          <AlertWarningIcon className="text-warning flex-shrink-0" />
                          {item}
                        </span>
                      </Tooltip>
                    ) : (
                      item
                    );
                  })}
                {products.length === 0 && (
                  <li className="small text-muted mb-2">-</li>
                )}
              </ul>
            </div>
            {emailCount > 0 && products.length > 0 && (
              <>
                <hr className="mb-2" />
                <div className="d-block py-2 small text-muted">
                  <strong>Summary</strong>
                </div>
                <div className="d-block mb-3 w-100">
                  <p className="small text-muted mb-2">
                    Assigning {products.length}{' '}
                    {products.length > 1 ? 'products' : 'product'} to{' '}
                    {emailCount} {emailCount > 1 ? 'people' : 'person'}
                  </p>
                </div>
              </>
            )}
            {isInviteStep && showContents && <hr className="mb-2" />}
            {!isInviteStep && <hr className="mb-2" />}
          </div>
          <div className="d-block flex-grow-1 py-2">
            <LinkButton
              disabled={disabled || products.length === 0}
              titleText={buttonText}
              icon={ArrowForwardIcon}
              iconAlign="right"
              variant="primary"
              onClick={() => onClick(products)}
              size="sm"
              className="w-100"
            />
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
